import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators, AbstractControl, ValidationErrors, AsyncValidatorFn } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable, of } from 'rxjs';
import { catchError, map, debounceTime, first } from 'rxjs/operators';

import { DialogService } from '@app-admin/dialogs/dialog.service';
import { CustomFormValidator } from '@app-shared/components/custom-validators';
import { UtilService } from '@app-shared/services/util.service';
import { GetService } from '@app/shared/table/get.service';
import { GetAllService } from '@app/shared/table/getAll.service';
import { UserService } from '@app/resources/users/users.service';

 @Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-add-admin',
    templateUrl: './add-admin.component.html',
    styleUrls: ['./add-admin.component.css'],
    standalone: false
})
export class AddAdminComponent implements OnInit {

  adminForm: UntypedFormGroup;
  pageType: string;
  dialogTitle: string;
  allStateNames: string[];
  districts: string[];
  protected allStates = {};
  serviceError: string;
  showPassword: boolean = false;
  showAddAdmin: boolean = true;

  constructor(
    @Inject(MAT_DIALOG_DATA) public modalData: any,
    public dialogRef: MatDialogRef<AddAdminComponent>,
    private fb: UntypedFormBuilder,
    private util: UtilService,
    private dialogSrvc: DialogService,
    private cdr: ChangeDetectorRef,
    private getAllService: GetAllService,
    private userService:UserService,
    private getService: GetService) { }

  ngOnInit(): void {
    this.pageType = 'id' in this.modalData ? 'Edit' : 'Add';
    const isSystemOrg = this.modalData && (this.modalData.orgId === -1 || this.modalData.organization_id === -1);
    if (this.pageType === 'Add') {
      this.dialogTitle = isSystemOrg ? 'Add System Super Admin' : 'Add Admin';
    } else {
      this.dialogTitle = isSystemOrg ? 'Edit System Super Admin' : 'Edit Admin';
    }

    const emailValidators = [
      Validators.required,
      Validators.pattern(/^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/)
    ];
    const emailAsyncValidators = this.pageType === 'Add' 
      ? [this.duplicateEmailValidator()]
      : [];

    const controls = {
      name: [this.pageType === 'Edit' ? this.modalData.full_name : '', [Validators.required]],
      email: [this.pageType === 'Edit' ? this.modalData.email : '', emailValidators, emailAsyncValidators],
      alternate_email: [this.pageType === 'Edit' ? this.modalData.alternate_email : '', [
        Validators.pattern(/^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/)]
      ],
      phone: [this.pageType === 'Edit' ? this.modalData.phone : '', [Validators.required]],
      alternate_phone: [this.pageType === 'Edit' ? this.modalData.alternate_phone : '', []],
      designation: [this.pageType === 'Edit' ? this.modalData.designation : '', [Validators.required]],
      havePinCode: [this.modalData.pincode_id ? 'true' : 'false'],
      pinCode: [this.pageType === 'Edit' ? this.modalData.pin_code : ''],
      state: ['', [Validators.required]],
      district: [{value:'', disabled: true}, [Validators.required]],
      pincode_id: [this.pageType === 'Edit' ? this.modalData.pincode_id : ''],
      geography_id: [this.pageType === 'Edit' ? this.modalData.geography_id : ''],
      is_org_admin: [this.pageType === 'Edit' ? this.modalData.is_org_admin : false],
    };
    if (this.pageType === 'Add') {
      controls['setPassword'] = ['false'];
      controls['password'] = ['', [Validators.required, CustomFormValidator.passwordStrengthChecker]];
    }
    else {
      controls['wantPassword'] = ['false'];
    }
    this.adminForm = this.fb.group(controls);

    this.getAllService.getAll('statesAndDistricts').subscribe(
      (data: any) => {
        this.allStates = data.body.responseBody;
        var allStatesObject = Object.values(this.allStates);
        var allStatesArray = allStatesObject.map( function(val){
          return [val['state_name'], val['districts']];
        })
        this.allStates=Object.fromEntries(allStatesArray)
        this.allStateNames = Object.keys(this.allStates);
        if (this.modalData.state_name) {
          this.districts = this.allStates[this.modalData.state_name].sort((a, b) => a > b ? 1 : -1);
          this.adminForm.patchValue({
            state: this.modalData.state_name,
            district: this.modalData.district_name
          });
          this.adminForm.get('district').enable();
          this.cdr.markForCheck();
          this.cdr.detectChanges();
        }
      },
      (err: any) => {
        this.serviceError = err.responseHead 
          ? this.util.getIntlErrorMessage("LIST_ORGS", err.responseHead.statusCode, err.responseHead.statusDesc)
          : err;
      }
    );
      this.adminForm.valueChanges.subscribe(() => {
        this.cdr.detectChanges();
    });
  }
  duplicateEmailValidator(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      if (!control.value) {
        return of(null);
      }

      const orgId = this.modalData.organization_id || this.modalData.orgId;
      const excludeUserId = this.modalData.id || null;

      return this.userService.checkDuplicateEmail(control.value, orgId, excludeUserId).pipe(
        debounceTime(400),
        map((response: any) => {
          if (response && response.body && response.body.responseBody && response.body.responseBody.length > 0) {
            return { duplicateEmail: true };
          }
          return null;
        }),
        catchError(() => {
          return of(null);
        }),
        first()
      );
    };
  }

stateDropdownList(e) {
    if (e.target.value) {
      this.adminForm.get('district').enable();
      this.adminForm.get('district').patchValue('');
      this.districts = this.allStates[e.target.value].sort((a, b) => a > b ? 1 : -1);
      // Add these lines to trigger validation
      this.adminForm.get('state').markAsTouched();
      this.adminForm.get('district').markAsTouched();
    }
    else {
      this.districts = [];
      this.adminForm.get('district').disable();
    }
    this.cdr.detectChanges(); // Add this to ensure change detection runs
}
  
  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  sendVerificationMailAfterCreate(email: string, fullName: string,org_id:any): Promise<void> {
  return new Promise((resolve, reject) => {
    this.userService.sendVerificationEmail(email, fullName,org_id).subscribe(
      () => resolve(),
      (err) => reject(err)
    );
  });
}


  createAdmin(){
    if (this.adminForm.invalid) {
      this.adminForm.markAllAsTouched();
      return;
    }
    const inputs = this.adminForm.value;
    const payload = {
      full_name: inputs.name,
      organization_id: this.modalData.organization_id || this.modalData.orgId,
      email: inputs.email,
      phone: inputs.phone,
      designation: inputs.designation,
      alternate_email: inputs.alternate_email,
      alternate_phone: inputs.alternate_phone,
      pincode_id: inputs.pincode_id,
      state_name: inputs.state,
      district_name: inputs.district,
      is_org_admin: inputs.is_org_admin,
    };
    // Include default role/category if provided (used for system super admin creation)
    if (this.modalData && this.modalData.user_role_id) {
      payload['user_role_id'] = this.modalData.user_role_id;
    }
    if (this.modalData && this.modalData.user_category_id) {
      payload['user_category_id'] = this.modalData.user_category_id;
    }
    payload.organization_id = parseInt(payload.organization_id, 10);
    if (this.modalData.id) {
      payload['id'] = this.modalData.id;
    }
    if (inputs['password']) {
      payload['password'] = inputs.password;
    }
    this.util.showLoader = true;
    this.dialogSrvc.createOrEditAdmin(payload).subscribe(
      async (data: any) => {
        const title = this.pageType === 'Add' ? 'Added' : 'Updated';
        const msgIdentifier = this.pageType === 'Add' ? 'ADD' : 'UPDATE';
        const responseHead = data.body.responseHead;

        let successMsg = responseHead
          ? this.util.getSuccessMessage(
              `${msgIdentifier}_ADMIN`,
              responseHead.statusCode,
              responseHead.statusDesc
            )
          : '';

        // 🔔 Send verification mail ONLY for Add
        if (this.pageType === 'Add') {
          try {
            await this.sendVerificationMailAfterCreate(
              payload.email,
              payload.full_name,
              payload.organization_id
            );
            successMsg += `\n\nA verification email has been sent to ${payload.email}.`;
          } catch (e) {
            successMsg += `\n\nAdmin created, but failed to send verification email.`;
          }
        }

        this.util.simpleDialog({
          title,
          message: successMsg,
        });
        this.util.showLoader = false;
        this.dialogRef.close();
      },
      (err: any) => {
        this.util.showLoader = false;

        let responseHead = err?.responseHead || err?.error?.responseHead || err?.body?.responseHead || {};
        let statusDesc = responseHead?.statusDesc || '';
        let statusCode = responseHead?.statusCode;

        if (typeof err === 'string') {
          statusDesc = err;
          statusCode = statusCode || '9998';
        } else if (!statusDesc && err?.error?.message) {
          statusDesc = err.error.message;
        }

        const isDuplicateEmail = /9998|organization\.users|duplicate|email/i.test(statusDesc);

        if (isDuplicateEmail) {
          const emailControl = this.adminForm.get('email');
          if (emailControl) {
            emailControl.setErrors({ duplicateEmail: true });
            emailControl.markAsTouched();
            emailControl.markAsDirty();
          }
          this.cdr.markForCheck();
          return;
        }

        this.serviceError = responseHead && statusDesc
          ? this.util.getIntlErrorMessage("LIST_ORGS", statusCode, statusDesc)
          : err;
        this.util.simpleDialog({
          title: 'Error',
          message: this.serviceError,
        });
      }
    );
  }

  updatePassword(){
    const isAuto = this.adminForm.get('setPassword').value === 'true';
    if (isAuto) {
      this.adminForm.get('password').patchValue(this.util.generatePassword(15));
    }
    else {
      this.adminForm.get('password').patchValue('');
    }
  }

updatePinCodeValidation() {
    if (this.adminForm.get('havePinCode').value === 'true') {
      this.adminForm.get('pinCode').setValidators([Validators.required, Validators.minLength(6)]);
    }
    else {
      this.adminForm.get('pinCode').clearValidators();
    }
    // Update these controls
    this.adminForm.get('pinCode').updateValueAndValidity();
    this.adminForm.get('state').updateValueAndValidity();
    this.adminForm.get('district').updateValueAndValidity();
    this.cdr.detectChanges();
}

  updatePwdFields() {
    if (this.adminForm.get('wantPassword').value === 'true') {
      this.adminForm.addControl('setPassword', new UntypedFormControl('false'));
      this.adminForm.addControl('password', new UntypedFormControl('', [Validators.required, CustomFormValidator.passwordStrengthChecker]));
    }
    else if (this.adminForm.contains('setPassword')){
      this.adminForm.removeControl('setPassword');
      this.adminForm.removeControl('password');
    }
    this.cdr.detectChanges();
  }
  onCloseAddAdmin() {
    // If this component was opened via MatDialog, close the dialog so the
    // caller's afterClosed() callback runs (which re-opens the Manage list).
    if (this.dialogRef && typeof this.dialogRef.close === 'function') {
      this.dialogRef.close();
    } else {
      // Fallback for legacy bootstrap-style usage
      this.showAddAdmin = false;
    }
  }

  loadGeography() {
    const pinCode = this.adminForm.get('pinCode').value;
    if (!pinCode) return;
    this.util.showLoader = true;
    this.serviceError = '';
    this.getService.get('geoByPincode', parseInt(pinCode)).subscribe(
      (data: any) => {
        const response = data.body.responseBody[0];
        this.adminForm.patchValue({
          state: response.state_name,
          district: response.district_name,
          geography_id: response.geography_id,
          pincode_id: response.pin_code_id
        });
        this.adminForm.get('district').enable();
        this.cdr.markForCheck();
        this.util.showLoader = false;
      },
      (err: any) => {
        this.util.showLoader = false;
        this.serviceError = err.responseHead 
          ? this.util.getIntlErrorMessage("PIN_FINDER", err.responseHead.statusCode, err.responseHead.statusDesc)
          : err;
      }
    );
  }

}